"use client";
import { useState, useCallback } from "react";
import axios from "axios";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";
const fmtDate = (d) =>
  !d
    ? "—"
    : new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(new Date(d));
const fmtC = (n) =>
  !Number.isFinite(n)
    ? "$0.00"
    : `$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const cls = (...c) => c.filter(Boolean).join(" ");

function Modal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60">
      <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-orange-400">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl leading-none"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function CORevisionPanelV3({
  changeOrderId,
  token,
  isApproved,
  onViewRevision,
  showAlert,
}) {
  const [revs, setRevs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/changeOrders3/${changeOrderId}/revisions`,
        { headers },
      );
      setRevs(r.data.revisions || []);
    } catch {
      setRevs([]);
    } finally {
      setLoading(false);
    }
  }, [changeOrderId]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && revs === null) load();
  };

  const create = async () => {
    setCreating(true);
    try {
      await axios.post(
        `${API}/changeOrders3/${changeOrderId}/revisions`,
        { label, note },
        { headers },
      );
      await load();
      setShowCreate(false);
      setLabel("");
      setNote("");
      showAlert?.("success", "", "Snapshot saved");
    } catch (err) {
      showAlert?.("error", "", err.response?.data?.message || "Failed");
    } finally {
      setCreating(false);
    }
  };

  const restore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      await axios.put(
        `${API}/changeOrders3/${changeOrderId}/revisions/${restoreTarget.revisionNumber}/restore`,
        {},
        { headers },
      );
      showAlert?.(
        "success",
        "",
        `Restored to ${restoreTarget.label || `R${restoreTarget.revisionNumber}`}`,
      );
      setRestoreTarget(null);
      await load();
    } catch (err) {
      showAlert?.("error", "", err.response?.data?.message || "Failed");
    } finally {
      setRestoring(false);
    }
  };

  const viewRevision = async (rev) => {
    if (rev.items?.length > 0) {
      onViewRevision?.(rev);
      return;
    }
    setLoadingDetail(rev.revisionNumber);
    try {
      const r = await axios.get(
        `${API}/changeOrders3/${changeOrderId}/revisions/${rev.revisionNumber}/full`,
        { headers },
      );
      onViewRevision?.({
        ...r.data.revision,
        label: rev.label,
        note: rev.note,
      });
    } catch {
      showAlert?.("error", "", "Failed to load revision");
    } finally {
      setLoadingDetail(null);
    }
  };

  return (
    <>
      <Modal
        open={showCreate}
        title="Save Snapshot"
        onClose={() => setShowCreate(false)}
      >
        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Label</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Before client review"
              className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="What changed…"
              className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setShowCreate(false)}
            className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={create}
            disabled={creating}
            className="px-4 py-2 text-sm text-white bg-orange-500 rounded hover:bg-orange-600 disabled:opacity-50"
          >
            {creating ? "Saving…" : "Save"}
          </button>
        </div>
      </Modal>

      <Modal
        open={!!restoreTarget}
        title="Restore Revision?"
        onClose={() => setRestoreTarget(null)}
      >
        {restoreTarget && (
          <>
            <div className="bg-gray-900/60 rounded-lg p-3 mb-4 border border-gray-700">
              <p className="text-sm font-medium text-white">
                {restoreTarget.label || `R${restoreTarget.revisionNumber}`}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {fmtDate(restoreTarget.createdAt)}
              </p>
              {restoreTarget.note && (
                <p className="text-xs text-gray-400 italic mt-1">
                  "{restoreTarget.note}"
                </p>
              )}
              <div className="flex gap-4 mt-2 text-xs">
                <span className="text-green-400">
                  Cost: {fmtC(restoreTarget.totalBudgetCost)}
                </span>
                <span className="text-orange-400">
                  Price: {fmtC(restoreTarget.totalCustomerPrice)}
                </span>
              </div>
            </div>
            <p className="text-xs text-yellow-400 mb-4">
              ⚠ This will overwrite current items and pricing sections.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRestoreTarget(null)}
                className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={restore}
                disabled={restoring}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {restoring ? "Restoring…" : "↩ Restore"}
              </button>
            </div>
          </>
        )}
      </Modal>

      <div className="bg-gray-800/60 rounded-lg border border-gray-700 overflow-hidden">
        <div
          className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-gray-700/40 transition-colors"
          onClick={toggle}
        >
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-orange-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm text-gray-300">Revision History</span>
            {revs !== null && (
              <span className="text-xs bg-gray-700 text-gray-400 px-2 py-0.5 rounded-full">
                {revs.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isApproved && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCreate(true);
                }}
                className="px-2.5 py-1 text-xs text-white bg-orange-500 rounded hover:bg-orange-600"
              >
                + Snapshot
              </button>
            )}
            <span className="text-gray-500 text-xs">{open ? "▲" : "▼"}</span>
          </div>
        </div>

        {open && (
          <div className="border-t border-gray-700">
            {loading ? (
              <div className="px-4 py-5 text-center text-gray-400 text-sm">
                Loading…
              </div>
            ) : !revs?.length ? (
              <div className="px-4 py-5 text-center text-gray-600 text-sm">
                No snapshots yet.
              </div>
            ) : (
              <div className="divide-y divide-gray-700/50">
                {[...revs].reverse().map((rev) => (
                  <div
                    key={rev.revisionNumber}
                    className="px-4 py-3 flex items-start justify-between gap-3 hover:bg-gray-700/30 transition-colors"
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-300 shrink-0">
                        R{rev.revisionNumber}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-200 truncate">
                          {rev.label || `Revision ${rev.revisionNumber}`}
                        </p>
                        <p className="text-xs text-gray-500">
                          {fmtDate(rev.createdAt)}
                        </p>
                        {rev.note && (
                          <p className="text-xs text-gray-500 italic mt-0.5">
                            "{rev.note}"
                          </p>
                        )}
                        <div className="flex gap-3 mt-1 text-xs">
                          <span className="text-green-400">
                            Cost: {fmtC(rev.totalBudgetCost)}
                          </span>
                          <span className="text-orange-400">
                            Price: {fmtC(rev.totalCustomerPrice)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => viewRevision(rev)}
                        disabled={loadingDetail === rev.revisionNumber}
                        className="px-2 py-1 text-xs text-gray-300 bg-gray-700 border border-gray-600 rounded hover:bg-gray-600 disabled:opacity-50"
                      >
                        {loadingDetail === rev.revisionNumber ? "…" : "👁 View"}
                      </button>
                      {!isApproved && (
                        <button
                          onClick={() => setRestoreTarget(rev)}
                          className="px-2 py-1 text-xs text-blue-400 bg-blue-900/20 border border-blue-700/40 rounded hover:bg-blue-900/40"
                        >
                          ↩ Restore
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
